import type { RuleGraphDto } from "@intelligems/ig-types";

import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { useAgenticLogicRuleVersionDetail } from "@src/api/agentic-logic/get-agentic-logic-rule-version-detail";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";

/**
 * The rule's Selection logic graph: the draft's artifact when one exists,
 * else the active version's — fetched only then, on the same query key the
 * version drawer uses, so the cache is shared. Both are SAVED artifacts; the
 * unsaved editor buffer never shows here.
 *
 * One derivation for everything that reads the graph off the page tree (the
 * canvas, the scenario generator, the cart-test card in the rail), so they
 * cannot disagree about which graph the rule currently has.
 */
export const useRuleSelectionGraph = (
  ruleId: string | undefined
): {
  graph: RuleGraphDto | null;
  /** The published fallback is still on its way; the draft has no graph. */
  isPublishedLoading: boolean;
} => {
  const draftQuery = useAgenticLogicRuleDraft(ruleId);
  const versionsQuery = useAgenticLogicRuleVersions(ruleId);
  const draftGraph = draftQuery.data?.draft?.graph ?? null;
  const activeVersion =
    versionsQuery.data?.versions.find(
      (version) => version.status === "active"
    ) ?? null;
  const publishedGraphVersion =
    draftQuery.data?.draft != null ? null : (activeVersion?.version ?? null);
  const versionDetailQuery = useAgenticLogicRuleVersionDetail(
    ruleId,
    publishedGraphVersion
  );
  const publishedGraph = versionDetailQuery.data?.detail.graph ?? null;
  return {
    graph: draftQuery.data?.draft != null ? draftGraph : publishedGraph,
    isPublishedLoading:
      publishedGraphVersion !== null && versionDetailQuery.isLoading,
  };
};
